import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const services = [
  { icon: "ri-home-heart-line", key: "residential" },
  { icon: "ri-building-4-line", key: "commercial" },
  { icon: "ri-sparkling-2-line", key: "deep" },
  { icon: "ri-door-open-line", key: "moveinout" },
  { icon: "ri-hotel-bed-line", key: "airbnb" },
  { icon: "ri-computer-line", key: "office" },
];

const ServicesPreviewSection = () => {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll(".service-card");
            cards.forEach((card, i) => {
              setTimeout(() => {
                card.classList.add("opacity-100", "translate-y-0");
                card.classList.remove("opacity-0", "translate-y-8");
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-24 bg-[#F5F5F5]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-3 block">
            {t("services_label")}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0F2A44] mb-4">
            {t("services_headline")}
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
            {t("services_sub")}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {services.map((s) => (
            <div
              key={s.key}
              className="service-card opacity-0 translate-y-8 transition-all duration-500 bg-white rounded-2xl p-7 group hover:-translate-y-2 cursor-default"
              style={{ boxShadow: "0 2px 20px rgba(0,0,0,0.06)" }}
            >
              {/* Icon */}
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#D4AF37]/10 mb-5 group-hover:bg-[#D4AF37]/20 transition-colors duration-300">
                <i className={`${s.icon} text-2xl text-[#D4AF37]`} />
              </div>
              {/* Title */}
              <h3 className="text-lg font-bold text-[#0F2A44] mb-2">
                {t(`service_${s.key}`)}
              </h3>
              {/* Desc */}
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                {t(`service_${s.key}_desc`)}
              </p>
              {/* Link */}
              <Link
                to="/services"
                className="inline-flex items-center gap-1.5 text-[#D4AF37] text-sm font-semibold hover:gap-3 transition-all duration-200"
              >
                {t("services_learn_more")}
                <i className="ri-arrow-right-line text-sm" />
              </Link>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-[#0F2A44] hover:bg-[#1a4a7a] text-white font-semibold px-8 py-4 rounded-full transition-all duration-200 hover:scale-105 whitespace-nowrap"
          >
            View All Services
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesPreviewSection;
